import { createContext, useContext, ReactNode } from 'react'
import { useColumnsPicker, UseColumnsPickerReturn } from '../hooks/use-columns-picker'
import { ITableColumnsType } from '../types'
import { useStyle } from '../hooks/use-style'
import { useUserPreferences, UseUserPreferencesReturn, TablePreferencesConfig } from '../hooks/use-user-preferences'
import React from 'react'

interface TableContextType<T = any> extends UseColumnsPickerReturn<T> {
  styles: ReturnType<typeof useStyle>['styles']
  preferencesConfig?: TablePreferencesConfig
  preferences?: UseUserPreferencesReturn
  initialColumns: ITableColumnsType<T>
  disabledPickerColumns?: string[]
}

const TableContext = createContext<TableContextType | null>(null)

interface TableProviderProps<T = any> {
  children: ReactNode
  columns: ITableColumnsType<T>
  preferences?: TablePreferencesConfig
  disabledPickerColumns?: string[]
}

export function TableProvider<T = any>({
  children,
  columns: initialColumns,
  preferences: preferencesConfig,
  disabledPickerColumns,
}: TableProviderProps<T>) {
  const userPreferences = useUserPreferences(preferencesConfig)
  const columnPicker = useColumnsPicker({ initialColumns, preferences: userPreferences })
  const { styles } = useStyle()

  return (
    <TableContext.Provider
      value={{
        ...columnPicker,
        styles,
        preferencesConfig: preferencesConfig,
        preferences: userPreferences,
        initialColumns,
        disabledPickerColumns,
      }}
    >
      {children}
    </TableContext.Provider>
  )
}

export function useTableContext<T = any>(): TableContextType<T> {
  const context = useContext(TableContext)
  if (!context) {
    throw new Error('useTableContext must be used within a TableProvider')
  }
  return context
}
